import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { CheckProvider } from "./contexts/CheckContext";

import Header from "./components/Header";
import Footer from "./components/Footer";
import StickyMobileCTA from "./components/StickyMobileCTA";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";

import LandingPage from "./pages/LandingPage";
import CheckPage from "./pages/CheckPage";
import TeaserResultPage from "./pages/TeaserResultPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import PackagesPage from "./pages/PackagesPage";
import LeadFormPage from "./pages/LeadFormPage";
import AccountPage from "./pages/AccountPage";
import RatgeberPage from "./pages/RatgeberPage";
import FAQPage from "./pages/FAQPage";
import DokumenteTeaserPage from "./pages/DokumenteTeaserPage";
import DocumentsPage from "./pages/DocumentsPage";
import { DatenschutzPage, ImpressumPage } from "./pages/LegalPages";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminLeadDetailPage from "./pages/AdminLeadDetailPage";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [pathname]);
    return null;
}

function AppRouter() {
    const location = useLocation();
    // Emergent OAuth: handle session_id fragment (can arrive on ANY path since
    // redirect_url is window.location.origin + "/auth/callback"; still keep synchronous check)
    if (location.hash?.includes("session_id=") && !location.pathname.startsWith("/auth/callback")) {
        return <AuthCallback />;
    }

    return (
        <>
            <Header />
            <main className="pb-24 lg:pb-0">
                <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/check" element={<CheckPage />} />
                    <Route path="/teaser" element={<TeaserResultPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />

                    <Route
                        path="/ergebnis"
                        element={
                            <ProtectedRoute>
                                <DashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/konto"
                        element={
                            <ProtectedRoute>
                                <AccountPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route path="/pakete" element={<PackagesPage />} />
                    <Route path="/kontakt" element={<LeadFormPage />} />
                    <Route path="/ratgeber" element={<RatgeberPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/dokumente" element={<DokumenteTeaserPage />} />
                    <Route
                        path="/meine-dokumente"
                        element={
                            <ProtectedRoute>
                                <DocumentsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/datenschutz" element={<DatenschutzPage />} />
                    <Route path="/impressum" element={<ImpressumPage />} />

                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminDashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/leads/:leadId"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminLeadDetailPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route path="*" element={<LandingPage />} />
                </Routes>
            </main>
            <Footer />
            <StickyMobileCTA />
        </>
    );
}

export default function App() {
    return (
        <div className="App min-h-screen">
            <HelmetProvider>
                <BrowserRouter>
                    <AuthProvider>
                        <CheckProvider>
                            <ScrollToTop />
                            <AppRouter />
                            <Toaster position="top-center" richColors />
                        </CheckProvider>
                    </AuthProvider>
                </BrowserRouter>
            </HelmetProvider>
        </div>
    );
}
